import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ThumbsUp, ThumbsDown, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import { isMongoObjectIdString } from '../utils/mongoId';

const RatingMetrics = ({ messageId, isExpert = false }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const fetchMetrics = async () => {
    if (!messageId || !isMongoObjectIdString(messageId)) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
      const response = await fetch(`${API_BASE}/ratings/response/${messageId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 404 || response.status === 422) {
          // No message/ratings yet, or malformed id (should not happen with client guard)
          setMetrics(null);
          return;
        } else if (response.status === 403) {
          // Forbidden - user doesn't have permission to view ratings
          setMetrics(null);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setMetrics(data);
    } catch (err) {
      console.error('Failed to fetch rating metrics:', err);
      setError('Failed to load rating metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, [messageId]);

  // Don't show anything if no metrics and not loading
  if (!metrics && !loading && !error) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-sm text-slate-500 mt-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-500"></div>
        <span>Loading ratings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-500 mt-2">
        {error}
      </div>
    );
  }

  if (!metrics || metrics.total_ratings === 0) {
    return null;
  }

  const thumbsUpPercentage = metrics.total_ratings > 0 
    ? Math.round((metrics.thumbs_up_count / metrics.total_ratings) * 100) 
    : 0;

  return (
    <div className="mt-3">
      {/* Summary */}
      <div className="flex items-center space-x-4 text-sm">
        <div className="flex items-center space-x-1">
          <ThumbsUp className="w-4 h-4 text-emerald-600" />
          <span className="text-emerald-600 font-medium">{metrics.thumbs_up_count}</span>
        </div>
        
        <div className="flex items-center space-x-1">
          <ThumbsDown className="w-4 h-4 text-red-600" />
          <span className="text-red-600 font-medium">{metrics.thumbs_down_count}</span>
        </div>
        
        <div className="text-slate-500">
          {metrics.total_ratings} rating{metrics.total_ratings !== 1 ? 's' : ''}
        </div>
        
        {metrics.total_ratings > 0 && (
          <div className="text-slate-500">
            ({thumbsUpPercentage}% positive)
          </div>
        )}
      </div>

      {/* Details Toggle */}
      {metrics.feedback_entries && metrics.feedback_entries.length > 0 && (
        <div className="mt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDetails(!showDetails)}
            className="text-slate-600 hover:text-slate-800 p-0 h-auto"
          >
            <MessageSquare className="w-4 h-4 mr-1" />
            <span className="text-sm">
              {showDetails ? 'Hide' : 'Show'} feedback ({metrics.feedback_entries.length})
            </span>
            {showDetails ? (
              <ChevronUp className="w-4 h-4 ml-1" />
            ) : (
              <ChevronDown className="w-4 h-4 ml-1" />
            )}
          </Button>
        </div>
      )}

      {/* Detailed Feedback */}
      {showDetails && metrics.feedback_entries && metrics.feedback_entries.length > 0 && (
        <Card className="mt-3 border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">
              Expert Feedback
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {metrics.feedback_entries.map((entry, index) => (
                <div key={index} className="border-l-2 pl-3 py-2 border-slate-100">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-xs font-medium text-slate-600">
                      {entry.expert_email}
                    </span>
                    <div className="flex items-center space-x-1">
                      {entry.rating === 'thumbs_up' ? (
                        <ThumbsUp className="w-3 h-3 text-emerald-600" />
                      ) : (
                        <ThumbsDown className="w-3 h-3 text-red-600" />
                      )}
                    </div>
                    <span className="text-xs text-slate-500">
                      {new Date(entry.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {entry.feedback}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RatingMetrics;
